import { createContext, useContext, useState, useRef, useEffect, useCallback } from "react";

const AudioContext = createContext();

const RATES = [0.75, 1, 1.25, 1.5, 1.75, 2];

const AudioProvider = ({ children }) => {
  // Single audio engine — survives every page navigation
  const audioRef = useRef(typeof Audio !== "undefined" ? new Audio() : null);

  const [currentTrack, setCurrentTrack] = useState(null);
  const [nextTrack, setNextTrack] = useState(null); // optional next-up for autoplay
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(() => {
    try { const v = parseFloat(localStorage.getItem("ris_volume") || "1"); return isFinite(v) ? v : 1; } catch { return 1; }
  });
  const [playbackRate, setPlaybackRate] = useState(() => {
    try { const r = parseFloat(localStorage.getItem("ris_playback_rate") || "1"); return RATES.includes(r) ? r : 1; } catch { return 1; }
  });
  const [autoPlayNext, setAutoPlayNext] = useState(() => {
    try { return localStorage.getItem("ris_autoplay") === "true"; } catch { return false; }
  });
  const [onTrackEnd, setOnTrackEnd] = useState(null); // optional callback when audio ends (e.g. for nav)

  const autoPlayRef = useRef(autoPlayNext);
  const nextTrackRef = useRef(nextTrack);
  const onEndRef = useRef(onTrackEnd);
  const currentTrackRef = useRef(currentTrack);

  useEffect(() => { autoPlayRef.current = autoPlayNext; }, [autoPlayNext]);
  useEffect(() => { nextTrackRef.current = nextTrack; }, [nextTrack]);
  useEffect(() => { onEndRef.current = onTrackEnd; }, [onTrackEnd]);
  useEffect(() => { currentTrackRef.current = currentTrack; }, [currentTrack]);

  // Apply rate and volume whenever they change
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = playbackRate;
    try { localStorage.setItem("ris_playback_rate", String(playbackRate)); } catch {}
  }, [playbackRate]);
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
    try { localStorage.setItem("ris_volume", String(volume)); } catch {}
  }, [volume]);

  // Bind core listeners ONCE (audio element exists for lifetime of provider)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => setProgress(audio.currentTime);
    const onMeta = () => setDuration(audio.duration || 0);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      // Clear saved position so next play starts from 0
      if (currentTrackRef.current) savePosition(currentTrackRef.current.id, 0);
      // Auto-play next if enabled + nextTrack present
      if (autoPlayRef.current && nextTrackRef.current) {
        const t = nextTrackRef.current;
        // Slight delay so UI can settle
        setTimeout(() => {
          playTrackRef.current(t);
          if (onEndRef.current) onEndRef.current(t);
        }, 250);
      } else if (onEndRef.current) {
        onEndRef.current(null);
      }
    };
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("durationchange", onMeta);
    audio.addEventListener("canplay", onMeta);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("durationchange", onMeta);
      audio.removeEventListener("canplay", onMeta);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  // ---- Resume-from-position ----
  // Persist progress per episode so users resume where they left off.
  const POSITIONS_KEY = "ris_episode_positions_v1";
  const loadPositions = () => {
    try { return JSON.parse(localStorage.getItem(POSITIONS_KEY) || "{}"); } catch { return {}; }
  };
  const savePosition = (trackId, time) => {
    if (!trackId) return;
    if (!isFinite(time) || time < 3) return; // ignore tiny progress
    try {
      const p = loadPositions();
      p[trackId] = { t: Math.floor(time), at: Date.now() };
      // keep only last 200 entries
      const entries = Object.entries(p).sort((a, b) => (b[1].at || 0) - (a[1].at || 0)).slice(0, 200);
      localStorage.setItem(POSITIONS_KEY, JSON.stringify(Object.fromEntries(entries)));
    } catch {}
  };
  const getPosition = (trackId) => {
    if (!trackId) return 0;
    const p = loadPositions();
    return p[trackId]?.t || 0;
  };

  // Periodically save progress while playing
  const lastSaveRef = useRef(0);
  useEffect(() => {
    if (!isPlaying || !currentTrack) return;
    const id = setInterval(() => {
      const audio = audioRef.current;
      if (!audio) return;
      const now = audio.currentTime;
      // throttle so we don't churn localStorage
      if (Math.abs(now - lastSaveRef.current) >= 5) {
        savePosition(currentTrack.id, now);
        lastSaveRef.current = now;
      }
    }, 3000);
    return () => clearInterval(id);
  }, [isPlaying, currentTrack?.id]);

  // Save on pause too
  useEffect(() => {
    if (isPlaying) return;
    const audio = audioRef.current;
    if (audio && currentTrack && audio.currentTime > 0) {
      savePosition(currentTrack.id, audio.currentTime);
    }
  }, [isPlaying, currentTrack?.id]);
  // -------------------------------

  // Stable playTrack via ref so onEnded handler can call it
  const playTrackRef = useRef(null);

  /**
   * Play a track. Track shape:
   *   { id, audio_url, title, series, season, episode_number, thumbnail_url, route }
   * Optional second arg: next track for auto-play chain.
   */
  const playTrack = useCallback((track, next = null) => {
    if (!track || !track.audio_url) return;
    const audio = audioRef.current;
    if (!audio) return;
    setNextTrack(next);

    // Same track? toggle
    if (currentTrack && currentTrack.id === track.id) {
      if (isPlaying) {
        audio.pause();
      } else {
        audio.play().catch(() => {});
      }
      return;
    }
    setCurrentTrack(track);
    audio.src = track.audio_url;
    audio.volume = volume;
    audio.playbackRate = playbackRate;

    // Resume from saved position if any (after metadata loads)
    const savedPos = getPosition(track.id);
    if (savedPos > 0) {
      const onMeta = () => {
        // Don't resume past the very end
        const dur = audio.duration || 0;
        const target = (dur && savedPos < dur - 5) ? savedPos : 0;
        if (target > 0) audio.currentTime = target;
      };
      if (audio.readyState >= 1 && audio.duration) onMeta();
      else audio.addEventListener("loadedmetadata", onMeta, { once: true });
    }

    audio.play().catch(() => {});
  }, [currentTrack, isPlaying, volume, playbackRate]);

  useEffect(() => { playTrackRef.current = playTrack; }, [playTrack]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;
    if (audio.paused) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  }, [currentTrack]);

  const seek = useCallback((time) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!isFinite(time)) return;
    audio.currentTime = Math.max(0, Math.min((audio.duration || 0), time));
    setProgress(audio.currentTime);
  }, []);

  const skipBy = useCallback((deltaSec) => {
    const audio = audioRef.current;
    if (!audio) return;
    seek(audio.currentTime + deltaSec);
  }, [seek]);

  const cyclePlaybackRate = useCallback(() => {
    setPlaybackRate(prev => {
      const idx = RATES.indexOf(prev);
      return RATES[(idx + 1) % RATES.length];
    });
  }, []);

  const changeVolume = useCallback((vol) => {
    setVolume(Math.max(0, Math.min(1, vol)));
  }, []);

  const toggleAutoPlayNext = useCallback(() => {
    setAutoPlayNext(prev => {
      const next = !prev;
      try { localStorage.setItem("ris_autoplay", String(next)); } catch {}
      return next;
    });
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    }
    setCurrentTrack(null);
    setNextTrack(null);
    setIsPlaying(false);
    setProgress(0);
    setDuration(0);
  }, []);

  return (
    <AudioContext.Provider value={{
      currentTrack, nextTrack, isPlaying, progress, duration, volume, playbackRate,
      autoPlayNext,
      playTrack, togglePlay, seek, skipBy, cyclePlaybackRate, changeVolume,
      toggleAutoPlayNext, setOnTrackEnd, setNextTrack, stop, getPosition,
      RATES,
    }}>
      {children}
    </AudioContext.Provider>
  );
};

const useAudio = () => useContext(AudioContext);

export { AudioContext, AudioProvider, useAudio };
